/**
 * Reset Password Page
 * Execute password reset with token from email
 */

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { apiClient } from '../../lib/api'
import { AuthShell, Field, Notice, Spinner } from './authParts'

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  })
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({})
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!token) {
      setError('無効なリセットリンクです')
    }
  }, [token])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    setError('')
    setFieldErrors(prev => ({ ...prev, [e.target.name]: undefined }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!token) {
      setError('無効なリセットリンクです')
      return
    }

    const errs: { newPassword?: string; confirmPassword?: string } = {}
    if (!formData.newPassword) errs.newPassword = '新しいパスワードを入力してください'
    else if (formData.newPassword.length < 8) errs.newPassword = '8文字以上で入力してください'
    if (!formData.confirmPassword) errs.confirmPassword = '確認のためもう一度入力してください'
    else if (formData.newPassword !== formData.confirmPassword) errs.confirmPassword = '上のパスワードと一致していません。同じものを入力してください'
    setFieldErrors(errs)
    if (errs.newPassword || errs.confirmPassword) return

    setIsLoading(true)

    try {
      const response = await apiClient.post('/auth/reset-password', {
        token,
        newPassword: formData.newPassword,
      })

      if (response.success) {
        setSuccess(true)
        setTimeout(() => {
          navigate('/login', { replace: true })
        }, 3000)
      } else {
        setError(response.error || 'パスワードのリセットに失敗しました')
      }
    } catch (err) {
      setError('リセット中にエラーが発生しました')
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <AuthShell title="パスワードを再設定しました">
        <Notice tone="ok">新しいパスワードでログインできます。まもなくログイン画面に移動します。</Notice>
        <Link to="/login" className="gm-btn gm-btn-primary gm-btn-block">ログイン画面へ</Link>
      </AuthShell>
    )
  }

  // リンクが無効（トークンなし）: 入力欄は出さず、直し方（再依頼）を案内する
  if (!token) {
    return (
      <AuthShell title="リンクが無効です">
        <Notice tone="bad">再設定のリンクが無効か、有効期限が切れています。もう一度、再設定のメールを依頼してください。</Notice>
        <Link to="/forgot-password" className="gm-btn gm-btn-primary gm-btn-block">再設定のメールを依頼する</Link>
        <p className="gm-auth-foot"><Link to="/login">ログイン画面に戻る</Link></p>
      </AuthShell>
    )
  }

  return (
    <AuthShell title="新しいパスワードを設定" lead="8文字以上のパスワードを入力してください。">
      <form onSubmit={handleSubmit} className="grid gap-4" noValidate>
        <Field
          type="password"
          id="newPassword"
          label="新しいパスワード"
          value={formData.newPassword}
          onChange={handleChange}
          autoComplete="new-password"
          disabled={isLoading}
          autoFocus
          error={fieldErrors.newPassword}
        />
        <Field
          type="password"
          id="confirmPassword"
          label="パスワード（確認）"
          value={formData.confirmPassword}
          onChange={handleChange}
          autoComplete="new-password"
          disabled={isLoading}
          error={fieldErrors.confirmPassword}
        />

        {error && <Notice tone="bad">{error}</Notice>}

        <button type="submit" disabled={isLoading} className="gm-btn gm-btn-primary gm-btn-block">
          {isLoading ? <Spinner /> : null}
          <span>{isLoading ? '設定しています' : 'パスワードを設定する'}</span>
        </button>

        <p className="gm-auth-foot"><Link to="/login">ログイン画面に戻る</Link></p>
      </form>
    </AuthShell>
  )
}
